/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import React, { PropsWithChildren } from "react";
import { Alert, Button, SxProps, Typography } from "@mui/material";
import { MinimalCase } from "../../../types/Entities/MinimalCase";
import { Link } from "react-router";
import { getCaseLink } from "../../../services/productService";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import { sxToArray } from "../../../services/utils";

type ProductType = MinimalCase | AbstractCase;

type IncompleteCaseWarningProps = {
  product: ProductType;
  sx?: SxProps;
};

export default function IncompleteCaseWarning({ product, sx = {} }: IncompleteCaseWarningProps) {
  if (product.isCustomerDataCompleted) {
    return null;
  }

  if (product.categoryChange) {
    return (
      <IncompleteCaseAlertBase product={product} sx={sx}>
        <Typography variant={"h6"}>Angaben ergänzen für weitere Unterstützung</Typography>
        <Typography sx={{
          marginBottom: "16px"
        }}>
          In Ihrem Fall ist nach der Erstberatung nun weitere Unterstützung gewünscht.{" "}
          <strong>Damit wir weiter für Sie tätig werden können, müssen Sie bitte weitere Angaben zu Ihrem Fall ergänzen und uns die Vollmacht erteilen.</strong>
        </Typography>
        <Typography sx={{
          marginBottom: "16px"
        }}>
          Keine Sorge: Ihre bisherigen Angaben und Dokumente sind nach wie vor vorhanden. Sie können diese also gerne weiter benutzen oder gegebenenfalls um
          neue Informationen und Dokumente ergänzen.
        </Typography>
      </IncompleteCaseAlertBase>
    );
  }
  return (
    <IncompleteCaseAlertBase product={product} sx={sx}>
      <Typography variant={"h6"}>Bitte vervollständigen Sie zuerst Ihren Fall</Typography>
      <Typography sx={{
        marginBottom: "16px"
      }}>
        Damit wir Ihnen helfen können, benötigen wir noch Angaben von Ihnen. <strong>Dies dauert keine 5 Minuten!</strong> Sobald uns Ihr Fall vollständig
        vorliegt, können Sie eine Beratung buchen, Ihren Fall prüfen oder Schreiben durch uns verfassen lassen.
      </Typography>
    </IncompleteCaseAlertBase>
  );
}

function IncompleteCaseAlertBase({ children, product, sx }: PropsWithChildren<{ product: ProductType; sx: SxProps }>) {
  return (
    <Alert severity={"error"} sx={[{ marginY: "1rem", paddingRight: "34px" }, ...sxToArray(sx)]}>
      {children}
      <Typography sx={{
        marginBottom: "16px"
      }}>
        <strong>Sie sind bei Angaben unsicher?</strong> Das ist gar kein Problem! Befüllen Sie alles bestmöglich und haben Sie keine Sorge vor „falschen“
        Angaben. Sie können uns im Formular per Freitext weitere Informationen und Hinweise geben. Ihr Fall wird immer von Experten geprüft, die jederzeit für
        Sie Angaben ergänzen und korrigieren können.
      </Typography>
      <Typography>
        <strong>Klicken Sie zum Vervollständigen bitte auf:</strong>
      </Typography>
      <Button variant={"contained"} fullWidth component={Link} to={getCaseLink(product) + "/antrag"}>
        Fall vervollständigen
      </Button>
    </Alert>
  );
}
